import {
    CreateUpdateBookingMeetingDetails,
    CreateUpdateBookingResourceTableSetups,
} from "../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { OrderType } from "../../../modules/payments/orderTypes"
import { MeetingServiceOrderDetails } from "../dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"
import { ProductsTableItem } from "../../../modules/merchant/admin/products/productsTypes"
import { OutlookSessionOrder } from "../../../modules/order/pages/outlookCreateOrderTypes"
import { MonetaryAmount } from "../../../modules/currency/currencyTypes"
import { OrganizerType } from "../../types/OrganizerTypes"
import { UserType } from "../../../modules/login/loginTypes"
import { ExtraServiceActionType } from "../../../modules/merchant/admin/extraServices/extraServicesTypes"

export enum CreateOrderDialogType {
    ORDER = "order",
    CREDIT_NOTE = "credit-note",
    SUPPLEMENTARY_ORDER = "supplementary-order",
}

export enum OrderLineType {
    EXTRA_SERVICE = "EXTRA_SERVICE",
}

export enum CreateOrderDialogMode {
    CREATE = "create",
    EDIT = "edit",
    DUPLICATE = "duplicate",
}
export enum CreateOrderFlowType {
    OUTLOOK = "outlook",
}

export enum CreateOrderOutlookFlowType {
    WEB = "outlook-web",
    VSTO = "outlook-vsto",
}

export enum CreateOrderDialogFlowMode {
    MEETING_SERVICE = "meetingService",
    RESOURCE_BOOKING = "resourceBooking",
}

export type CreateOrderDialogFlow = {
    flow: CreateOrderFlowType.OUTLOOK | string | null
    mode: CreateOrderDialogFlowMode | null
    vstoFlowParam?: string
    order?: OutlookSessionOrder
    calendarAppointmentId?: string
    /**
     * Whether the meeting lives in a calendar shared with other people, told to us by the add-in —
     * only it can read that. It decides the organizer layout: a shared calendar has to take the one
     * id-less organizer slot the API tolerates, the user's own calendar does not.
     *
     * `undefined` means the add-in did not report it (a version older than the flag) — not "own
     * calendar". Read it through `isSharedCalendarOrder`, which handles that case; and never infer
     * it from `ORGANIZER.email`, since the add-in sends that address for every calendar.
     */
    isSharedCalendar?: boolean
}

export type ServiceCenterTimeline = Readonly<{
    id: number
    uid: string
    name: string
    resources: ServiceCenterTimelineResource[]
    address?: AddressInfo
    floorPlan?: FloorPlanInfo
    messaging?: MessagingInfo
}>

type AddressInfo = Readonly<{
    street: string
    streetNumber: string
    postalCode: string
    city: string
}>

type FloorPlanInfo = Readonly<{
    imageUrl: string
}>

type MessagingInfo = Readonly<{
    enabled: boolean
}>

export type ServiceCenterTimelineResource = Readonly<{
    id: number
    uid: string
    name: string
    displayName: string
    type: CreateOrderResourceType

    colorCode?: string
    buffer?: number
    outOfService?: OutOfServiceResource
    accessControl?: ResourceAccessControl
    image?: {
        url: string
    }
    room?: ResourceRoom
}>
export type ResourceRoom = Readonly<{
    avEquipments?: TimelineResourceFixedAvEquipment[]
    floorDetails?: {
        name: string
        value: number
    }
    isPrivateOffice: boolean
    meetingUsage?: {
        internal: boolean
        external: boolean
    }
    seats?: number
    tableSetups?: ResourceTableSetups[]
}>

export type TimelineResourceFixedAvEquipment = Readonly<{
    id: number
    name: string
}>

export type OutOfServiceResource = Readonly<{
    active: boolean
    message: string
}>

export type ResourceTableSetups = Readonly<{
    key: string
    name: string
    imageUrl: string
}>

export type ResourceAccessControl = Readonly<{
    isLimited: boolean
    hasAccess: boolean
}>

export enum ResourceType {
    ROOM = "ROOM",
    AV = "AV",
    CAR = "CAR",
}
export type CreateOrderResourceType = Readonly<{
    id: number
    name: string
}>

export type EventsForServiceCenter = Readonly<{
    orderId: number
    resourceIds: number[]
    startDateTime: string
    endDateTime: string
    subject?: string
    canEdit?: boolean
    outOfService?: boolean
    host?: string
}>

// FILTERS
export type ResourceFilterElementValue = Readonly<{
    key: string | number
    value: string | number
    imageUrl?: string
}>

export type ResourceFilterElement = Readonly<{
    elementType:
        | "MultiSelect"
        | "CheckBox"
        //     | "ComboBox"
        //     | "DropDown"
        | "PlusMinusButton"
        | "TimeSlider"
        | "DateTimePicker"
        | "Switcher"
        | string
    identifier: string
    name: string
    translationKey: string
    values?: ResourceFilterElementValue[]
}>

export type ResourceFilterType = Readonly<{
    filterElements: ResourceFilterElement[]
    name: string
    translationKey: string
}>

export type ResourceFiltersTypes = Readonly<{
    filters: ResourceFiltersSelectedItem[]
    search: string
    isBookedPeriod?: boolean
    onlyAvailableForMeeting?: boolean
}>

export type ResourceFiltersSelectedItem = Readonly<{
    identifier: string
    isRemovable?: boolean
    values: ResourceFilterElementValue[]
}>

export type CreateUpdateBookingOrganizer = Readonly<{
    id?: number
    organizerType: OrganizerType
    name?: string
    email?: string
}>

export type CreateUpdateBooking = Readonly<{
    order?: { id: number }
    orderType: OrderType
    organizers: CreateUpdateBookingOrganizer[]
    meetingDetails: CreateUpdateBookingMeetingDetails
    messageToServiceCenter?: string
    bookingDetails: {
        resources: CreateUpdateBookingResourceTableSetups[]
    }
}>

/** Which save path produced a booking response — `createBooking` or `updateBooking`. */
export type BookingSaveKind = Readonly<{
    /** No order existed before this save, so the success dialog may show the new order number. */
    isNewOrder: boolean
    /** An existing booking was saved again, i.e. its time or resources may have moved. */
    isBookingUpdate: boolean
}>

export type CreateUpdateBookingOrder = Readonly<{
    id: number
    uid: string
    bookingDetails?: {
        resources: CreateUpdateBookingResourceTableSetups[]
    }
    orderType: OrderType
    meetingDetails: CreateUpdateBookingMeetingDetails
    organizers: CreateUpdateBookingOrganizer[]
    messageToServiceCenter?: string
}>
export enum CalendarImportLinkRel {
    ICS_APPOINTMENT = "ICS_APPOINTMENT",
    ICS_MEETING_REQUEST = "ICS_MEETING_REQUEST",
}
export type CreateUpdateBookingCalendarImportLinks = Readonly<{
    rel: CalendarImportLinkRel
    href: string
    translationKey: string
}>

//  Create / update Meeting Service Order

export type CreateUpdateMeetingServiceOrder = MeetingServiceOrderDetails & {
    calendarAppointmentId?: string
}

// Free / Busy Schedule
export type AvailabilityDetails = Readonly<{
    startTime: string
    endTime: string
    availabilityViewInterval: number
}>
export type ResourceScheduleItem = Readonly<{
    resource: ResourceDetails
    availabilities: AvailabilityItem[]
}>
export type ResourceDetails = Readonly<{
    uid: string
    id?: number
    name: string
    resourceNumber: string
    buffer: number
}>

export type AvailabilityItem = Readonly<{
    startTime: string
    endTime: string
    availabilityView: string // 0 = free, everything else = busy
}>

type BookingOrderMeetingDetails = Readonly<{
    meetingName: string
    location: string
    meetingTimeAsText: string
    startDateTime: string
    endDateTime: string
    meetingDuration: number
}>
type BookingOrderOrganizer = Readonly<{
    organizerType: string
    id: number
    uid: string
    name?: string
    email?: string
    businessPhone?: string
    mobilePhone?: string
    company?: {
        id: number
        name: string
    }
}>

export type BookingOrderDetailsItem = Readonly<{
    id: number
    uid: string
    meetingDetails: BookingOrderMeetingDetails
    organizers: BookingOrderOrganizer[]
}>

// FAVORITES

export type SaveProductFavoriteBody = Readonly<{
    id: number
    productType: string // MEETING_SERVICE
}>
export type ProductFavorite = Readonly<{
    id: number
    userDetails: {
        favoriteType: string
        favoriteId: number
        canFavorite: boolean
    }
    productType: string
}>

export type ProductWithQuantity = ProductsTableItem & {
    quantity: number
    isExtraService?: boolean
    isAutoAddedExtraService?: boolean
    isMeetingTimeRequired?: boolean
    isMeetingLocationRequired?: boolean
    priceListId?: number
    itemPrice?: MonetaryAmount
    action?: ExtraServiceActionType
    extraServicePricingType?: "PER_SEAT" | "FLAT_FEE"
    extraServicePricePerSeat?: MonetaryAmount
    extraServiceMinPrice?: MonetaryAmount
    extraServiceMaxPrice?: MonetaryAmount
}

export type SelectedPriceListItem = Readonly<{
    merchantId: number
    merchantName: string
    priceListId: number
    priceListName: string
    shopId: number
    isOnlyOnePriceList: boolean
    priceListIncludeVat: boolean
}>

export enum MeetingServicePriceListStatus {
    UNSELECTED = "UNSELECTED",
    VALIDATING = "VALIDATING",
    READY = "READY",
    NO_PRODUCTS = "NO_PRODUCTS",
    ERROR = "ERROR",
}

export type MeetingServicePriceListState = Readonly<{
    item: SelectedPriceListItem | null
    status: MeetingServicePriceListStatus
}>

export type ResourceItem = Readonly<{
    id: number
    title: string
    // hasMessagingEnabled?: boolean
    tableSetups?: ResourceTableSetups[]
    type: CreateOrderResourceType
}>

export type SelectedMainUserState = Readonly<{
    userId: number
    displayName: string
    companyId: number
    userType: UserType
}>

export type ExtraServiceDetails = Readonly<{
    id: number
    serviceType: string
    pricingType: "PER_SEAT" | "FLAT_FEE"
    name: string
    description: string
    productGroupCode: string | { id: number; number: string; name: string }
    minimumPrice?: MonetaryAmount
    maximumPrice?: MonetaryAmount
    pricePerSeat?: MonetaryAmount
    price?: MonetaryAmount
    action?: ExtraServiceActionType
    isMandatory?: boolean
    isMeetingTimeRequired?: boolean
    isMeetingLocationRequired?: boolean
    isActive?: boolean
}>

export enum MessagePriority {
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
}
export type Message = Readonly<{
    id: number
    priority: MessagePriority
    text: string
    publishDate: string //"2024-01-04T11:23:22"
}>

export type OrganizerSelectedValue = Readonly<{
    id?: number
    name?: string
    email?: string
}>

export type BillReceiversItemMS = Readonly<{
    id: number
    type: string
    vatnumber: string
    name: string
    address: BillReceiverAddressMS
    endpoint: BillReceiverEndpointMS
    contactDetails?: BillReceiversItemContactDetails
}>

type BillReceiverAddressMS = Readonly<{
    displayName: string
}>
export enum BillReceiverTypeMS {
    EAN_NUMBER = "EAN",
    EMAIL_ADDRESS = "EMAIL",
}
export type EndpointEan = Readonly<{
    type: BillReceiverTypeMS.EAN_NUMBER
    ean: string
}>
export type EndpointEmail = Readonly<{
    type: BillReceiverTypeMS.EMAIL_ADDRESS
    email: string
}>

export type BillReceiverEndpointMS = EndpointEmail | EndpointEan

export type BillReceiversItemContactDetails = Readonly<{
    name: string
    email?: string
    phoneNumber?: string
}>

export enum SatelliteType {
    EAT_AT_LOCATION = "EAT_AT_LOCATION",
    PICKUP_LOCATION = "PICKUP_LOCATION",
    DELIVERY_LOCATION = "DELIVERY_LOCATION",
}
export type SatelliteDetails = Readonly<{
    id: number
    types: ReadonlyArray<SatelliteType>
    name: string
    description: string
}>

export type AccountDimensionDetailsForUser = Readonly<{
    id: number
    name: string
    costCenters: AccountDimensionCostCenter[]
}>

type AccountDimensionCostCenter = Readonly<{
    value: string
    name: string
}>

export type AvailableModules = Readonly<{
    isBookingActive: boolean
    isCateringActive: boolean
}>

export type ServiceCenterResourceDetails = Readonly<{
    id: number
    description: string
    email: string
    imageUrl?: string
    name: string
    resourceNumber: string
    resourceTypeId: number
    room: ServiceCenterResourceDetailsRoom
    serviceCenterName: string
}>

type ServiceCenterResourceDetailsRoom = Readonly<{
    floor: number
    isPrivateOffice: boolean
    seats: number
    sittings: number
    squareMeters: number
    tableSettings?: string[]
    tableSetups?: { key: string; name: string }[]
    walkDirection: string
}>
